function getExtension(filename = '') {
  const normalized = filename.toLowerCase();
  const parts = normalized.split('.');
  return parts.length > 1 ? parts.pop() : '';
}

function isImageFile(file) {
  const extension = getExtension(file?.name);
  return ['jpg', 'jpeg', 'png', 'webp'].includes(extension);
}

function isPdfFile(file) {
  return getExtension(file?.name) === 'pdf';
}

function isDocxFile(file) {
  return getExtension(file?.name) === 'docx';
}

function isSupportedPageCountFile(file) {
  return isImageFile(file) || isPdfFile(file) || isDocxFile(file);
}

function countPdfPages(arrayBuffer) {
  const text = new TextDecoder('latin1').decode(arrayBuffer);
  const matches = text.match(/\/Type\s*\/Page\b/g);
  return matches?.length || 0;
}

function findEndOfCentralDirectory(bytes) {
  for (let index = bytes.length - 22; index >= 0; index -= 1) {
    if (
      bytes[index] === 0x50 &&
      bytes[index + 1] === 0x4b &&
      bytes[index + 2] === 0x05 &&
      bytes[index + 3] === 0x06
    ) {
      return index;
    }
  }

  throw new Error('Invalid DOCX file.');
}

async function inflateRawBytes(bytes) {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('This browser cannot read DOCX page counts yet.');
  }

  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function extractZipEntryText(arrayBuffer, entryName) {
  const bytes = new Uint8Array(arrayBuffer);
  const view = new DataView(arrayBuffer);
  const eocdOffset = findEndOfCentralDirectory(bytes);
  const centralDirectorySize = view.getUint32(eocdOffset + 12, true);
  const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true);
  const decoder = new TextDecoder();

  let offset = centralDirectoryOffset;
  const end = centralDirectoryOffset + centralDirectorySize;

  while (offset < end) {
    if (view.getUint32(offset, true) !== 0x02014b50) {
      throw new Error('Invalid DOCX structure.');
    }

    const compressionMethod = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const fileNameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);

    const fileNameStart = offset + 46;
    const fileNameEnd = fileNameStart + fileNameLength;
    const fileName = decoder.decode(bytes.slice(fileNameStart, fileNameEnd));

    if (fileName === entryName) {
      if (view.getUint32(localHeaderOffset, true) !== 0x04034b50) {
        throw new Error('Invalid DOCX entry.');
      }

      const localFileNameLength = view.getUint16(localHeaderOffset + 26, true);
      const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
      const dataStart = localHeaderOffset + 30 + localFileNameLength + localExtraLength;
      const compressedBytes = bytes.slice(dataStart, dataStart + compressedSize);

      if (compressionMethod === 0) {
        return decoder.decode(compressedBytes);
      }

      if (compressionMethod === 8) {
        const inflatedBytes = await inflateRawBytes(compressedBytes);
        return decoder.decode(inflatedBytes);
      }

      throw new Error('Unsupported DOCX compression method.');
    }

    offset = fileNameEnd + extraLength + commentLength;
  }

  return null;
}

async function countDocxPages(arrayBuffer) {
  const appXml = await extractZipEntryText(arrayBuffer, 'docProps/app.xml');
  const appPageMatch = appXml?.match(/<Pages>(\d+)<\/Pages>/i);
  const appPages = Number(appPageMatch?.[1] || 0);

  if (appPages > 0) {
    return appPages;
  }

  const documentXml = await extractZipEntryText(arrayBuffer, 'word/document.xml');
  if (!documentXml) {
    return 0;
  }

  const explicitPageBreaks = (documentXml.match(/w:type="page"/g) || []).length;
  const renderedPageBreaks = (documentXml.match(/lastRenderedPageBreak/g) || []).length;
  return Math.max(1, explicitPageBreaks + renderedPageBreaks + 1);
}

export async function getFilePageCount(file) {
  if (!isSupportedPageCountFile(file)) {
    throw new Error(`${file.name} is not supported. Upload PDF, DOCX, JPG, JPEG, PNG, or WEBP files only.`);
  }

  if (isImageFile(file)) {
    return 1;
  }

  const arrayBuffer = await file.arrayBuffer();

  if (isPdfFile(file)) {
    const pages = countPdfPages(arrayBuffer);
    if (pages > 0) {
      return pages;
    }
    throw new Error(`Could not read page count for ${file.name}. Please re-save the PDF and try again.`);
  }

  if (isDocxFile(file)) {
    const pages = await countDocxPages(arrayBuffer);
    if (pages > 0) {
      return pages;
    }
    throw new Error(`Could not read page count for ${file.name}. Please re-save the DOCX and try again.`);
  }

  throw new Error(`${file.name} is not supported for page counting.`);
}
